import { useAvailableStandardOrderBumbalDates } from "app/hooks/checkout/useAvailableStandardOrderBumbalDates";
import { useGetBumbalBeforeDays } from "app/hooks/checkout/useGetBumbalBeforeDays";
import classNames from "classnames";
import { getDay, getMonth } from "date-fns";
import nl from "date-fns/locale/nl";
import DatePicker, { registerLocale } from "react-datepicker";
import Spinner from "shared/ui/spinner";
import { months } from "shared/utils/date";
import { DatePickerFooterStandardProduct } from "../DatePickerFooterStandardProduct";
import { useCallback } from "react";

registerLocale("nl", nl);

export type BumbalType = "shipment" | "placement";
export interface ReactDatePickerCustomHeaderProps {
  monthDate: Date;
  date: Date;
  changeYear(year: number): void;
  changeMonth(month: number): void;
  customHeaderCount: number;
  decreaseMonth(): void;
  increaseMonth(): void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
  decreaseYear(): void;
  increaseYear(): void;
  prevYearButtonDisabled: boolean;
  nextYearButtonDisabled: boolean;
}

const monthDiff = (a: Date, b: Date) =>
  Math.abs(
    (a.getFullYear() - b.getFullYear()) * 12 + (a.getMonth() - b.getMonth()),
  );

const CheckoutPanelShipmentDeliveryDatePicker = ({
  order_id,
  setDate,
  selectedDateProp,
  formattedDate,
  bumbalType,
}: {
  order_id: string;
  setDate: (date: Date | null) => void;
  selectedDateProp: Date | null;
  formattedDate: string;
  bumbalType: BumbalType;
}) => {
  const { bumbalBeforeDays } = useGetBumbalBeforeDays(order_id, bumbalType);

  const days = bumbalBeforeDays?.days;

  const {
    status,
    availableDatesContainer,
    firstAvailableDate,
    setPageDate,
    availableMonths,
    firstMonth,
    pageDate,
  } = useAvailableStandardOrderBumbalDates({
    type: bumbalType,
    delayedDays: days,
    setDate,
    selectedDateProp,
    order_id,
  });

  // Statuses
  const isOngoingQuery = status !== "success";
  const isLoadingState = status === "loading";
  const isErrorQuery = status === "error";

  const inPossibleDates = (date: Date) => {
    if (!availableDatesContainer) return false;

    const targetYear = date.getFullYear();
    const targetMonth = date.getMonth();
    const targetDay = date.getDate();

    return availableDatesContainer.some((d) => {
      const tempDate = new Date(d);
      return (
        tempDate.getFullYear() === targetYear &&
        tempDate.getMonth() === targetMonth &&
        tempDate.getDate() === targetDay
      );
    });
  };

  const filterDeliveryMethodDates = (date: Date): boolean => {
    const day = getDay(date);
    return day !== 6 && day !== 0 && inPossibleDates(date);
  };

  const handleMonthChange = (date: Date) => {
    // Return on initial render
    if (date.getMonth() === firstAvailableDate?.getMonth()) {
      return;
    }

    // Update the date controlling the availableBumbalDates query
    setPageDate(
      (prev: Date) => new Date(date.getFullYear(), date.getMonth(), 1),
    );
  };

  // Custom header for date picker
  const customHeaderElement = useCallback(
    ({
      date,
      decreaseMonth,
      increaseMonth,
      changeMonth,
    }: ReactDatePickerCustomHeaderProps) => {
      const disablePrevMonth = months[getMonth(date)] === availableMonths[0];
      const disableLastMonth =
        months[getMonth(date)] === availableMonths[availableMonths.length - 1];
      return (
        <>
          <div
            className={
              "gg-mb-8 gg-grid gg-w-full gg-grid-cols-3 gg-text-left gg-font-bold"
            }
          >
            <div className="gg-flex gg-w-full gg-items-center">
              {!disablePrevMonth && (
                <button
                  className="gg-transition gg-duration-300 gg-ease-in-out hover:gg-scale-125 hover:gg-text-dark-blue"
                  onClick={decreaseMonth}
                  disabled={disablePrevMonth}
                >
                  {"<"}
                </button>
              )}
            </div>

            <div className="gg-mr-8 gg-flex gg-w-full gg-items-center gg-justify-center">
              <select
                className="gg-peer gg-flex gg-h-full gg-w-36 gg-cursor-pointer gg-rounded-lg gg-border-r-8 gg-border-transparent gg-px-4 gg-py-3 gg-text-sm gg-text-dark-blue gg-outline gg-outline-2 gg-outline-gray-400 gg-transition-all focus:gg-outline-2 focus:gg-outline-dark-blue focus:gg-ring-dark-blue"
                value={months[getMonth(date)]}
                onChange={({ target: { value } }) =>
                  changeMonth(months.indexOf(value))
                }
              >
                {availableMonths.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="gg-flex gg-w-full gg-items-center gg-justify-end">
              {!disableLastMonth && (
                <button
                  className="gg-whitespace-nowrap gg-rounded-sm gg-text-slate-800 gg-transition gg-duration-300 gg-ease-in-out hover:gg-scale-125"
                  onClick={increaseMonth}
                  disabled={disableLastMonth}
                >
                  {">"}
                </button>
              )}
            </div>
          </div>
        </>
      );
    },
    [availableMonths],
  );

  // SelectedDate
  const selectedDate = selectedDateProp ? selectedDateProp : firstAvailableDate;

  // Show last month message if the difference between the first month and the current month is greater than 3
  const showLastMonthMessage = monthDiff(firstMonth(), pageDate) > 3;

  // Check if the month is not available
  const isMonthNotAvailable = firstAvailableDate === null;

  return (
    <>
      <div className="gg-col-span-12 lg:gg-col-span-8">
        <div className="gg-grid gg-w-full gg-grid-cols-12 gg-divide-y gg-divide-dark-blue gg-bg-grey-500 md:gg-divide-y-0">
          <div className="gg-relative gg-col-span-12 gg-px-1 gg-py-5 sm:gg-px-8 sm:gg-py-8">
            {isOngoingQuery ? (
              <DatePicker
                selected={null} // Set selected date to the pageDate state
                onChange={(date) => {}}
                inline
                locale={"nl"}
                readOnly={true}
                disabled={true}
                filterDate={() => {
                  return false;
                }}
                renderCustomHeader={customHeaderElement}
              >
                {isLoadingState && (
                  <div
                    className={classNames(
                      "gg-z-2 gg-absolute gg-inset-0 gg-grid gg-h-96 gg-w-full gg-place-items-center gg-content-center gg-items-center gg-gap-x-4 gg-gap-y-5 gg-px-5 gg-backdrop-blur-lg",
                    )}
                  >
                    <Spinner className="gg-shrink-0 gg-text-black" />
                    <h3 className="gg-text-base gg-font-semibold gg-text-dark-blue">
                      Momentje, de beschikbare mogelijkheden worden voor je
                      opgehaald.
                    </h3>
                  </div>
                )}

                {isErrorQuery && (
                  <div
                    className={classNames(
                      "gg-z-2 gg-absolute gg-inset-0 gg-grid gg-h-96 gg-w-full gg-place-items-center gg-content-center gg-items-center gg-gap-x-4 gg-px-5 gg-backdrop-blur-lg",
                    )}
                  >
                    <h3>
                      Er is iets misgegaan met uw betaling. Neem voor meer
                      informatie contact op met klantenservice.
                    </h3>
                  </div>
                )}
              </DatePicker>
            ) : (
              <DatePicker
                selected={selectedDate} // Set selected date to the pageDate state
                onChange={(date) => setDate(date)}
                inline
                locale={"nl"}
                onMonthChange={handleMonthChange}
                minDate={firstAvailableDate}
                filterDate={filterDeliveryMethodDates}
                renderCustomHeader={customHeaderElement}
              />
            )}
          </div>

          <DatePickerFooterStandardProduct
            isMonthNotAvailable={isMonthNotAvailable}
            formattedDate={formattedDate}
            showLastMonthMessage={showLastMonthMessage}
          />
        </div>
      </div>
    </>
  );
};
export default CheckoutPanelShipmentDeliveryDatePicker;
